import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import Modal from '../components/Modal';
import CacheStatusBar from '../components/CacheStatusBar';
import './ProductionBatchPage.css';

function ProductionBatchPage() {
  const { currentUser, isAdmin, canPerformAction } = useAuth();
  const dataCache = useDataCache();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');

  // State for Create Batch Modal
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [manuallyExcluded, setManuallyExcluded] = useState(new Set());
  const [isMarkingReceived, setIsMarkingReceived] = useState(null); // batchId
  const [isDeleting, setIsDeleting] = useState(null); // Store batchId being deleted
  const [isCreatingPoForBatch, setIsCreatingPoForBatch] = useState(null); // batchId
  const [batchQualityToCreate, setBatchQualityToCreate] = useState('');
  const [isFirstBatch, setIsFirstBatch] = useState(false);
  const [startFromNumber, setStartFromNumber] = useState('1');
  const [existingListFilter, setExistingListFilter] = useState('all'); // all | batch | purchase_order
  const [showAllBatches, setShowAllBatches] = useState(false);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState('batches'); // 'batches' | 'pending_report'
  const [pendingReportData, setPendingReportData] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isCopyingImage, setIsCopyingImage] = useState(false);
  const [copySuccessMsg, setCopySuccessMsg] = useState('');
  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const [targetsConfig, setTargetsConfig] = useState({ Polyester: 3, Pure: 5, Mixed: 4, default: 5 });
  const [isSavingTargets, setIsSavingTargets] = useState(false);
  const [targetSaveMsg, setTargetSaveMsg] = useState('');
  const [expandedTimelines, setExpandedTimelines] = useState(new Set());
  const [timelinesData, setTimelinesData] = useState({});

  const canViewTimeline = isAdmin || canPerformAction('batches.timeline') || canPerformAction('batches.create');

  const handleToggleTimeline = useCallback(async (e, batchId) => {
    e.stopPropagation();
    setExpandedTimelines((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });

    if (!timelinesData[batchId] || timelinesData[batchId].error) {
      setTimelinesData((prev) => ({
        ...prev,
        [batchId]: { loading: true, error: '', data: null },
      }));

      try {
        const token = currentUser ? await currentUser.getIdToken() : '';
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`/api/v9/production-batches/${batchId}/timeline`, { headers });
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json?.error?.message || json?.message || 'Failed to load timeline.');
        }

        setTimelinesData((prev) => ({
          ...prev,
          [batchId]: { loading: false, error: '', data: json.timeline || [] },
        }));
      } catch (err) {
        console.error(`Error loading timeline for batch ${batchId}:`, err);
        setTimelinesData((prev) => ({
          ...prev,
          [batchId]: { loading: false, error: err.message || 'Failed to load timeline.', data: null },
        }));
      }
    }
  }, [currentUser, timelinesData]);

  const isBatchEligibleForTimeline = useCallback((batch) => {
    if (!batch?.createdAt) return false;
    const createdDate = new Date(batch.createdAt);
    // Cutoff: 1st September 2026 (00:00:00 local/IST)
    const cutoff = new Date('2026-09-01T00:00:00+05:30');
    return !isNaN(createdDate.getTime()) && createdDate.getTime() >= cutoff.getTime();
  }, []);

  const formatBatchOrderNumber = useCallback((orderNumber, source) => {
    const raw = String(orderNumber || '').trim();
    if (!raw) return '';
    if (String(source || '').trim() !== 'Shopify') return raw;
    return raw.endsWith('00') ? raw : `${raw}00`;
  }, []);

  const getAllocatedQtyForItem = useCallback((item) => {
    return (item?.sourceOrders || []).reduce((sum, so) => sum + (parseFloat(so?.allocatedQuantity) || 0), 0);
  }, []);

  const deriveBatchStatusFromItems = useCallback((items = []) => {
    if (!Array.isArray(items) || items.length === 0) return 'Created';
    const activeItems = items.filter((it) => it?.status !== 'unavailable');
    if (activeItems.length === 0) return 'Received';
    const epsilon = 0.0001;
    const hasAnyAllocated = activeItems.some((item) => getAllocatedQtyForItem(item) > epsilon);
    const allFullyAllocated = activeItems.every((item) => {
      const total = parseFloat(item?.totalQuantity) || 0;
      const allocated = getAllocatedQtyForItem(item);
      return allocated + epsilon >= total;
    });
    if (allFullyAllocated) return 'Received';
    if (hasAnyAllocated) return 'Partially Received';
    return 'Created';
  }, [getAllocatedQtyForItem]);

  const invalidateOrdersAndInventoryCaches = useCallback(() => {
    dataCache.invalidatePrefix('orders:');
    dataCache.invalidate('inventoryPieces:/api/v1/inventory/all-pieces');
    dataCache.invalidatePrefix('inventorySummary:');
    dataCache.invalidatePrefix('inventorySummarySearch:');
    dataCache.invalidatePrefix('inventoryLedger:');
  }, [dataCache]);

  const latestBatchIds = useMemo(() => {
    const latest = {};
    // The `batches` are already sorted by date descending from the backend.
    for (const batch of batches) {
        if (!latest[batch.batchQuality]) {
            latest[batch.batchQuality] = batch.batchId;
        }
    }
    return new Set(Object.values(latest));
  }, [batches]);

  const isPurchaseOrderBatch = useCallback((batch = {}) => {
    const batchId = String(batch?.batchId || '').trim().toUpperCase();
    const quality = String(batch?.batchQuality || '').trim().toLowerCase();
    const sourceType = String(batch?.sourceType || '').trim().toLowerCase();
    return batchId.startsWith('MIX-') || quality === 'mixed' || sourceType === 'po_bulk' || sourceType === 'po_single_order';
  }, []);

  const getPurchaseOrderBatchSubtitle = useCallback((batch = {}) => {
    if (!isPurchaseOrderBatch(batch)) return '';

    const reference = String(
      batch?.poReferenceNumber
      || batch?.zohoPurchaseOrder?.referenceNumber
      || batch?.referenceNumber
      || ''
    ).trim();
    if (reference) return `Ref: ${reference}`;

    const orderTokens = [];
    (Array.isArray(batch?.consolidatedItems) ? batch.consolidatedItems : []).forEach((item) => {
      const sourceOrders = Array.isArray(item?.sourceOrders) ? item.sourceOrders : [];
      sourceOrders.forEach((so) => {
        const raw = String(so?.orderNumber || '').trim().replace(/^#/, '');
        if (raw) orderTokens.push(raw);
      });
    });

    const uniqueOrders = Array.from(new Set(orderTokens));
    if (uniqueOrders.length === 0) return '';
    if (uniqueOrders.length === 1) return `Order: #${uniqueOrders[0]}`;
    return `Orders: #${uniqueOrders[0]} +${uniqueOrders.length - 1} more`;
  }, [isPurchaseOrderBatch]);

  const canViewCompleted = canPerformAction('batches.viewCompleted');
  const canViewPendingReport = canPerformAction('batches.viewPendingReport');

  const visibleBatches = useMemo(() => {
    if (!Array.isArray(batches)) return [];
    let scoped = batches;
    if (existingListFilter === 'purchase_order') {
      scoped = batches.filter((batch) => isPurchaseOrderBatch(batch));
    } else if (existingListFilter !== 'all') {
      scoped = batches.filter((batch) => {
        if (isPurchaseOrderBatch(batch)) return false;
        const quality = String(batch?.batchQuality || '').trim().toLowerCase();
        return quality === 'polyester' || quality === 'pure' || quality === 'plain';
      });
    }

    if (canViewCompleted && showAllBatches) return scoped;

    return scoped.filter((batch) => {
      const status = String(deriveBatchStatusFromItems(batch?.consolidatedItems || []) || '')
        .trim()
        .toLowerCase();
      return status === 'partially received' || status === 'pending' || status === 'created';
    });
  }, [batches, existingListFilter, showAllBatches, canViewCompleted, deriveBatchStatusFromItems, isPurchaseOrderBatch]);
  
  const fetchBatches = useCallback(async ({ asRefresh = false, force = false } = {}) => {
    if (!currentUser) return;
    // Show cached instantly to avoid reload flicker on route changes.
    const cacheKey = 'productionBatches:/api/v9/production-batches';
    const cachedEntry = dataCache.getCacheEntry(cacheKey);
    const cachedBatches = Array.isArray(cachedEntry?.value?.batches) ? cachedEntry.value.batches : [];
    if (cachedBatches.length > 0) {
      setBatches(cachedBatches);
      setIsLoading(false);
      if (!force && !cachedEntry.stale && !asRefresh) return;
    }

    setIsLoading(cachedBatches.length === 0);
    setError('');
    try {
      const { data } = await dataCache.fetchJsonCached(cacheKey, '/api/v9/production-batches', { force: force || asRefresh });
      setBatches(data.batches || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, dataCache]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const handleRefreshBatches = async () => {
    setIsRefreshing(true);
    try {
      await fetchBatches({ asRefresh: true, force: true });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDownloadImageReport = async () => {
    setIsDownloadingImage(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v9/production-batches/reports/pending-image', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to download image report.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending_batches_report_${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to download image report.');
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const fetchPendingReportData = useCallback(async () => {
    if (!currentUser) return;
    setIsLoadingReport(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v9/production-batches/reports/pending-data', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error?.message || data?.message || 'Failed to fetch report data.');
      }
      setPendingReportData(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load report data.');
    } finally {
      setIsLoadingReport(false);
    }
  }, [currentUser]);

  const handleCopyImageToClipboard = async () => {
    setIsCopyingImage(true);
    setCopySuccessMsg('');
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v9/production-batches/reports/pending-image', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate image.');
      const blob = await response.blob();

      if (!navigator.clipboard || !navigator.clipboard.write) {
        throw new Error('Clipboard image copying is not supported in this browser. Please use Download Image instead.');
      }
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      setCopySuccessMsg('Copied image to clipboard! You can paste directly into WhatsApp.');
      setTimeout(() => setCopySuccessMsg(''), 5000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to copy image to clipboard.');
    } finally {
      setIsCopyingImage(false);
    }
  };

  const handleOpenTargetModal = () => {
    if (pendingReportData?.targets) {
      setTargetsConfig({ ...pendingReportData.targets });
    }
    setTargetSaveMsg('');
    setIsTargetModalOpen(true);
  };

  const handleSaveTargets = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    setIsSavingTargets(true);
    setTargetSaveMsg('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v9/production-batches/settings/targets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ targets: targetsConfig }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error?.message || data?.message || 'Failed to save targets.');
      }
      setTargetSaveMsg('Target production days saved successfully!');
      setTimeout(() => {
        setIsTargetModalOpen(false);
        fetchPendingReportData();
      }, 1000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save target days.');
    } finally {
      setIsSavingTargets(false);
    }
  };

  const handlePrepareBatch = async (quality) => {
    if (!canPerformAction('batches.create')) return;
    setIsPreparing(true);
    setError('');
    setBatchQualityToCreate(quality);
    const previewEndpoint = '/api/v9/production-batches/preview';

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(previewEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ batchQuality: quality }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error?.message || data?.message || 'Failed to generate batch preview.');
      }

      const payload = (data && typeof data.result === 'object' && data.result)
        ? data.result
        : data;
      const previewItems = Array.isArray(payload?.consolidatedItems) ? payload.consolidatedItems : [];

      if (previewItems.length === 0) {
        alert(`No eligible items found for a '${quality}' batch.`);
        return;
      }

      if (payload?.isFirstBatch) {
        setIsFirstBatch(true);
        setStartFromNumber('1'); // Default to 1
      } else {
        setIsFirstBatch(false);
      }
      setPreviewData(payload);
      setManuallyExcluded(new Set()); // Reset exclusions when opening modal
      setShowPreviewModal(true);
    } catch (err) {
      setError(err.message); // Show error on the main page
    } finally {
      setIsPreparing(false);
    }
  };

	  const handleCreateBatch = async () => {
	    if (!canPerformAction('batches.create')) return;
    setIsCreating(true);
    setError('');
    const createEndpoint = '/api/v9/production-batches';

    const finalConsolidatedItems = previewData.consolidatedItems.map(item => {
      const includedSourceOrders = item.sourceOrders.filter(so => !manuallyExcluded.has(so.lineItemId));
      if (includedSourceOrders.length === 0) {
          return null;
      }
      return {
          ...item,
          sourceOrders: includedSourceOrders,
          totalQuantity: includedSourceOrders.reduce((sum, so) => sum + so.quantity, 0)
      };
    }).filter(Boolean);

    if (finalConsolidatedItems.length === 0) {
        setError('Cannot create an empty batch. Please include at least one order item.');
        setIsCreating(false);
        return;
    }
    const body = { batchQuality: batchQualityToCreate, consolidatedItems: finalConsolidatedItems };
    if (isFirstBatch) {
      if (!startFromNumber || isNaN(parseInt(startFromNumber, 10)) || parseInt(startFromNumber, 10) < 1) {
        setError('Please enter a valid starting batch number greater than 0.');
        setIsCreating(false);
        return;
      }
      body.startFrom = startFromNumber;
    }

	    try {
	      const token = await currentUser.getIdToken();
	      const response = await fetch(createEndpoint, {
	        method: 'POST',
	        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
	        body: JSON.stringify(body),
	      });
      const data = await response.json();
      const payload = (data && typeof data.result === 'object' && data.result)
        ? data.result
        : data;
      if (!response.ok || data?.ok === false || data?.success === false) {
        throw new Error(data?.error?.message || data?.message || 'Failed to create batch.');
      }
      
		      alert(
            data?.message
            || payload?.message
            || (payload?.batchId ? `Batch ${payload.batchId} created successfully.` : 'Batch created successfully.')
          );
		      setShowPreviewModal(false);
		      setPreviewData(null);
		      setIsFirstBatch(false);
		      dataCache.invalidate('productionBatches:/api/v9/production-batches');
		      invalidateOrdersAndInventoryCaches();
		      fetchBatches(); // Refresh the list
		    } catch (err) {
	      setError(err.message);
	    } finally {
	      setIsCreating(false);
    }
  };

	  const handleDeleteBatch = async (e, batchId) => {
    e.stopPropagation(); // Prevent row click navigation
    if (!canPerformAction('batches.delete') || isDeleting) return; // Prevent multiple deletes at once
    if (!window.confirm(`Are you sure you want to delete batch ${batchId}? This will remove it from all associated orders.`)) {
      return;
    }
    const typed = window.prompt(`Type "${batchId}" to confirm deletion.`);
    if (typed !== batchId) {
      setError('Batch delete cancelled. Confirmation text did not match batch id.');
      return;
    }

    setIsDeleting(batchId);
    setError('');

	    try {
	        const token = await currentUser.getIdToken();
	        const response = await fetch(`/api/v9/production-batches/${batchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to delete batch.');
        }

		        alert(data.message);
		        dataCache.invalidate('productionBatches:/api/v9/production-batches');
		        invalidateOrdersAndInventoryCaches();
		        fetchBatches(); // Refresh the list
		    } catch (err) {
	        setError(err.message);
	    } finally {
        setIsDeleting(null);
    }
  };

	  const handleMarkAsReceived = async (e, batchId) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to mark the entire batch ${batchId} as received? This will fulfill all associated orders.`)) {
      return;
    }

    setIsMarkingReceived(batchId);
    setError('');

	    try {
	      const token = await currentUser.getIdToken();
	      const response = await fetch(`/api/v9/production-batches/${batchId}/mark-as-received`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to mark batch as received.');
      
		      alert(result.message);
		      dataCache.invalidate('productionBatches:/api/v9/production-batches');
		      invalidateOrdersAndInventoryCaches();
		      fetchBatches();
		    } catch (err) {
	      setError(err.message);
	    } finally {
      setIsMarkingReceived(null);
    }
  };

	  const handleCreateBatchPo = async (e, batch) => {
    e.stopPropagation();
    if (!canPerformAction('batches.create') || !currentUser) return;
    if (!batch?.batchId) return;
    if (batch?.zohoPurchaseOrderId || batch?.zohoPurchaseOrder?.zohoPurchaseOrderId) {
      alert('Purchase order already created for this batch.');
      return;
    }
    if (!window.confirm(`Create Zoho Purchase Order for batch ${batch.batchId}?`)) return;

    setIsCreatingPoForBatch(batch.batchId);
    setError('');
	    try {
	      const token = await currentUser.getIdToken();
	      const resp = await fetch(`/api/v9/production-batches/${batch.batchId}/create-po`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
	      if (!resp.ok) throw new Error(data.message || 'Failed to create purchase order.');
		      alert(data.message || 'Purchase order created.');
		      dataCache.invalidate('productionBatches:/api/v9/production-batches');
		      invalidateOrdersAndInventoryCaches();
		      fetchBatches();
		    } catch (err) {
	      setError(err.message);
	    } finally {
	      setIsCreatingPoForBatch(null);
    }
  };

  const handleToggleGroupedExclusion = (lineItemIds = []) => {
    const ids = Array.from(new Set((lineItemIds || []).map((id) => String(id)).filter(Boolean)));
    if (ids.length === 0) return;
    setManuallyExcluded((prev) => {
      const next = new Set(prev);
      const allExcluded = ids.every((id) => next.has(id));
      if (allExcluded) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleRowClick = (batchId) => {
    navigate(`/batches/${batchId}`);
  };

  const renderPreview = () => {
    if (!previewData) return null;

    const parseOrderNum = (orderStr) => {
      const numMatch = String(orderStr || '').match(/(\d+)/);
      return numMatch ? Number(numMatch[1]) : null;
    };

    const includedItems = previewData.consolidatedItems
      .map(item => {
        const includedSourceOrders = item.sourceOrders.filter(so => !manuallyExcluded.has(so.lineItemId));
        // find latest order date among included source orders for sorting
        const latestOrderTs = Math.max(...includedSourceOrders.map(so => new Date(so.orderDate || 0).getTime()));
        const maxOrderNum = Math.max(
          ...includedSourceOrders
            .map((so) => parseOrderNum(so.orderNumber))
            .filter((n) => n !== null && !Number.isNaN(n)),
          -Infinity
        );
        return {
          ...item,
          sourceOrders: includedSourceOrders,
          _latestOrderTs: latestOrderTs,
          _maxOrderNum: maxOrderNum,
        };
      })
      .filter(item => item.sourceOrders.length > 0)
      .sort((a, b) => {
        // Primary: highest order number
        if (a._maxOrderNum !== -Infinity || b._maxOrderNum !== -Infinity) {
          return (b._maxOrderNum || -Infinity) - (a._maxOrderNum || -Infinity);
        }
        // Fallback: latest order date
        return b._latestOrderTs - a._latestOrderTs;
      })
      .map(({ _latestOrderTs, _maxOrderNum, ...rest }) => rest);
    const includedOrderIdsCount = new Set(includedItems.flatMap(item => item.sourceOrders.map(so => so.orderId))).size;

    const groupSourceOrders = (sourceOrders = []) => {
      const grouped = new Map();
      (sourceOrders || []).forEach((so) => {
        const orderId = String(so?.orderId || '').trim();
        const orderNumber = String(so?.orderNumber || '').trim();
        const source = String(so?.source || '').trim();
        const groupKey = `${source}::${orderId || orderNumber}`;
        const existing = grouped.get(groupKey) || {
          orderId: so?.orderId,
          orderNumber: so?.orderNumber,
          source: so?.source,
          quantity: 0,
          lineItemIds: [],
        };
        existing.quantity += Number(so?.quantity || 0);
        if (so?.lineItemId != null) existing.lineItemIds.push(String(so.lineItemId));
        grouped.set(groupKey, existing);
      });
      return Array.from(grouped.values()).map((g) => ({
        ...g,
        lineItemIds: Array.from(new Set(g.lineItemIds)),
      }));
    };

    const allIneligible = Array.isArray(previewData.ineligibleItems) ? previewData.ineligibleItems : [];
    const suppressedReasons = new Set([
      'Order is post-shipped',
      'Order is cancelled',
      'Already fulfilled',
      'Already in batch',
    ]);
    const inStockAlertsRaw = allIneligible.filter((item) => {
      const reason = String(item?.reason || '').trim().toLowerCase();
      return reason.includes('in stock');
    });
    const groupActionItems = (items = []) => {
      const grouped = new Map();
      (items || []).forEach((item) => {
        const orderNumber = String(item?.orderNumber || 'N/A').trim();
        const sku = String(item?.sku || 'N/A').trim().toUpperCase();
        const key = `${orderNumber}::${sku}`;
        const existing = grouped.get(key) || {
          orderNumber,
          sku,
          reason: item?.reason || '',
          count: 0,
        };
        existing.count += 1;
        grouped.set(key, existing);
      });
      return Array.from(grouped.values());
    };
    const inStockAlerts = groupActionItems(inStockAlertsRaw);
    const missingSpecAlerts = allIneligible.filter((item) =>
      String(item?.reason || '').toLowerCase().includes('missing fabric specification')
    );
    const visibleExcluded = allIneligible.filter((item) => {
      const reason = String(item?.reason || '');
      const reasonLower = reason.trim().toLowerCase();
      if (reason.toLowerCase().includes('missing fabric specification')) return false; // shown as alert cards
      if (reasonLower.includes('in stock')) return false; // shown as separate action-needed block
      if (reasonLower === 'before v9 cutover') return false; // shown as separate action-needed block
      return !Array.from(suppressedReasons).some((base) => reason.startsWith(base));
    });

    return (
      <div>
        {isFirstBatch && (
          <div className="start-from-section">
            <h4>First Batch of this Quality</h4>
            <p>This is the first time a '{batchQualityToCreate}' batch is being created. Please specify the starting number for this batch series (e.g., if your old system was at PURE-122, enter 123).</p>
            <div className="form-group">
              <label>Start Batch Number From</label>
              <input
                type="number"
                value={startFromNumber}
                onChange={(e) => setStartFromNumber(e.target.value)}
                min="1"
              />
            </div>
          </div>
        )}
        {inStockAlerts.length > 0 && (
          <>
            <h3 className="excluded-items-header excluded-items-header-stock">
              Action Needed: In Stock (Not Included) ({inStockAlerts.length})
            </h3>
            <div className="batch-preview-alerts stock-alerts">
              {inStockAlerts.map((item, index) => (
                <div key={`${item.orderNumber}-${item.sku}-${index}`} className="batch-preview-alert-item stock-alert-item">
                  <strong>Order #{item.orderNumber}</strong> • <span>{item.sku}</span> • <span>In stock</span>
                </div>
              ))}
            </div>
          </>
        )}

        <h3>Items to Include ({includedItems.length})</h3>
        <div className="preview-table-wrapper">
          <table className="preview-table">
            <thead>
              <tr>
                <th>SKU</th>
                 <th>Fabric Name (Office)</th> 
                <th>Total Quantity</th>
                <th>Order Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {previewData.consolidatedItems.map(item => {
                const includedSourceOrders = item.sourceOrders.filter(so => !manuallyExcluded.has(so.lineItemId));
                const groupedSourceOrders = groupSourceOrders(item.sourceOrders);
                const newTotalQuantity = includedSourceOrders.reduce((sum, so) => sum + so.quantity, 0);

                return (
                  <tr key={item.sku}>
                    <td>{item.sku}</td>
                    <td>{item.fabricName || ''}</td>
                    <td>{newTotalQuantity.toFixed(2)}m</td>
                    <td>
                      <ul className="order-breakdown-list">
                        {groupedSourceOrders.map((soGroup) => {
                          const isExcluded = soGroup.lineItemIds.every((id) => manuallyExcluded.has(id));
                          return (
                            <li key={`${soGroup.source || ''}:${soGroup.orderId || soGroup.orderNumber}`} className={isExcluded ? 'excluded' : ''}>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={!isExcluded}
                                  onChange={() => handleToggleGroupedExclusion(soGroup.lineItemIds)}
                                />
                                #{formatBatchOrderNumber(soGroup.orderNumber, soGroup.source)}: {soGroup.quantity.toFixed(2)}m
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                  </tr>
                )})}
            </tbody>
          </table>
        </div>
        <p>This batch will include items from {includedOrderIdsCount} unique orders.</p>

        {missingSpecAlerts.length > 0 && (
          <>
            <h3 className="excluded-items-header">Action Needed: Missing Fabric Specification ({missingSpecAlerts.length})</h3>
            <div className="batch-preview-alerts">
              {missingSpecAlerts.map((item, index) => (
                <div key={`${item.orderNumber}-${item.sku}-${index}`} className="batch-preview-alert-item">
                  <strong>Order #{item.orderNumber}</strong> • <span>{item.sku}</span> • <span>{item.reason}</span>
                </div>
              ))}
            </div>
          </>
        )}


        {visibleExcluded.length > 0 && (
          <>
            <h3 className="excluded-items-header">Excluded Items ({visibleExcluded.length})</h3>
            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>SKU</th>
                    <th>Reason for Exclusion</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleExcluded.map((item, index) => (
                    <tr key={index} className="ineligible-row">
                      <td>#{item.orderNumber}</td>
                      <td>{item.sku}</td>
                      <td>{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="batch-page-container">
      <div className="page-header">
        <h1>Production Batches</h1>
        <CacheStatusBar
          cacheKey="productionBatches:/api/v9/production-batches"
          onRefresh={handleRefreshBatches}
          isRefreshing={isRefreshing}
          hideStaleTag
        />
      </div>

      <div className="batch-view-tabs-nav">
        <button
          type="button"
          className={`batch-view-tab-btn ${activeTab === 'batches' ? 'active' : ''}`}
          onClick={() => setActiveTab('batches')}
        >
          📦 Batches Overview
        </button>
        <button
          type="button"
          className={`batch-view-tab-btn ${activeTab === 'pending_report' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('pending_report');
            fetchPendingReportData();
          }}
        >
          📊 Pending Production Report
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}

      {activeTab === 'batches' && (
        <>
          {canPerformAction('batches.create') && (
            <div className="batch-actions-section">
              <h2>Create New Batch</h2>
              <div className="batch-buttons">
                <button className="polyester-btn" onClick={() => handlePrepareBatch('Polyester')} disabled={isPreparing}>
                  {isPreparing && batchQualityToCreate === 'Polyester' ? 'Preparing...' : 'Create Polyester Batch'}
                </button>
                <button className="pure-btn" onClick={() => handlePrepareBatch('Pure')} disabled={isPreparing}>
                  {isPreparing && batchQualityToCreate === 'Pure' ? 'Preparing...' : 'Create Pure Batch'}
                </button>
              </div>
            </div>
          )}

          <div className="batch-list-section">
            <div className="batch-list-header">
              <div className="batch-list-title-wrap">
                <h2>Existing Batches</h2>
                {canViewCompleted && (
                  <button
                    type="button"
                    className="batch-filter-btn"
                    onClick={() => setShowAllBatches((prev) => !prev)}
                  >
                    {showAllBatches ? 'Show Pending Only' : 'Show All Batches'}
                  </button>
                )}
                {canViewPendingReport && (
                  <button
                    type="button"
                    className="batch-filter-btn image-report-btn"
                    onClick={() => {
                      setActiveTab('pending_report');
                      fetchPendingReportData();
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    📊 Open Pending Report
                  </button>
                )}
              </div>
              <div className="batch-list-filters">
                <button
                  type="button"
                  className={`batch-filter-btn ${existingListFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setExistingListFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`batch-filter-btn ${existingListFilter === 'batch' ? 'active' : ''}`}
                  onClick={() => setExistingListFilter('batch')}
                >
                  Batch
                </button>
                <button
                  type="button"
                  className={`batch-filter-btn ${existingListFilter === 'purchase_order' ? 'active' : ''}`}
                  onClick={() => setExistingListFilter('purchase_order')}
                >
                  Purchase Order
                </button>
              </div>
            </div>
            {isLoading ? <p>Loading batches...</p> : (
              <div className="table-wrapper">
                <table className="batches-table">
                  <thead>
                    <tr>
                      <th>Batch ID</th>
                      <th>Quality</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Item Count</th>
                      <th>Total Quantity</th>
                      {(canPerformAction('batches.delete') || canPerformAction('batches.create') || canViewTimeline) && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleBatches.length > 0 ? visibleBatches.map((batch, index) => (
                      (() => {
                        const displayStatus = deriveBatchStatusFromItems(batch?.consolidatedItems || []);
                        const isTimelineOpen = expandedTimelines.has(batch.batchId);
                        const totalCols = (canPerformAction('batches.delete') || canPerformAction('batches.create') || canViewTimeline) ? 7 : 6;
                        return (
                      <React.Fragment key={batch.batchId}>
                        <tr onClick={() => handleRowClick(batch.batchId)} className={`batch-row-clickable ${isTimelineOpen ? 'batch-row-expanded' : ''}`}>
                          <td>
                            <div className="batch-id-cell">
                              <div className="batch-id-main">{batch.batchId}</div>
                              {(() => {
                                const subtitle = getPurchaseOrderBatchSubtitle(batch);
                                return subtitle ? <div className="batch-id-sub">{subtitle}</div> : null;
                              })()}
                            </div>
                          </td>
                          <td>{batch.batchQuality}</td>
                          <td>
                            <span className={`status-tag ${displayStatus?.toLowerCase().replace(' ', '-')}`}>{displayStatus}</span>
                          </td>
                          <td>
                            <div className="batch-created-date">{new Date(batch.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                            {batch.tiffRunAt ? (
                              (() => {
                                const createdDate = new Date(batch.createdAt);
                                const runDate = new Date(batch.tiffRunAt);
                                const diffDays = Math.max(0, Math.round((runDate - createdDate) / (1000 * 60 * 60 * 24)));
                                const runDateFormatted = runDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                                const daysText = diffDays === 0 ? 'same day' : `${diffDays}d`;
                                return (
                                  <div className="batch-turnaround-subtext run-completed" title={`Processed on ${runDate.toLocaleString()}`}>
                                    ✓ Run: {runDateFormatted} ({daysText})
                                  </div>
                                );
                              })()
                            ) : (
                              (() => {
                                const createdDate = new Date(batch.createdAt);
                                const ageDays = Math.max(0, Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24)));
                                const isLegacyOrReceived = displayStatus === 'Partially Received' || displayStatus === 'Received';
                                if (isLegacyOrReceived) {
                                  return null;
                                }
                                return (
                                  <div className="batch-turnaround-subtext run-pending" title={`Created ${ageDays} days ago, awaiting run`}>
                                    ⏳ Awaiting run ({ageDays}d)
                                  </div>
                                );
                              })()
                            )}
                          </td>
                          <td>{batch.consolidatedItems.length}</td>
                          <td>{batch.consolidatedItems.reduce((sum, item) => sum + item.totalQuantity, 0).toFixed(2)}m</td>
                          {(canPerformAction('batches.delete') || canPerformAction('batches.create') || canViewTimeline) && (
                            <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                              {canPerformAction('batches.create') && (
                                <button
                                  onClick={(e) => handleCreateBatchPo(e, batch)}
                                  className="action-button export-btn"
                                  disabled={
                                    isCreatingPoForBatch === batch.batchId ||
                                    !!(batch?.zohoPurchaseOrderId || batch?.zohoPurchaseOrder?.zohoPurchaseOrderId)
                                  }
                                  title="Create Zoho Purchase Order"
                                >
                                  {batch?.zohoPurchaseOrderId || batch?.zohoPurchaseOrder?.zohoPurchaseOrderId
                                    ? `PO: ${batch?.poDisplayNumber || batch?.zohoPurchaseOrder?.poDisplayNumber || batch?.batchId || 'Created'}`
                                    : (isCreatingPoForBatch === batch.batchId ? 'Creating PO...' : 'Create PO')}
                                </button>
                              )}
                              {latestBatchIds.has(batch.batchId) && batch.status === 'Created' && displayStatus === 'Created' && canPerformAction('batches.delete') && (
                                <button
                                  onClick={(e) => handleDeleteBatch(e, batch.batchId)}
                                  className="delete-btn"
                                  disabled={!!isDeleting}
                                >
                                  {isDeleting === batch.batchId ? 'Deleting...' : 'Delete'}
                                </button>
                              )}
                              {displayStatus !== 'Received' && batch.status !== 'Received' && canPerformAction('batches.markReceived') && (
                                <button
                                  onClick={(e) => handleMarkAsReceived(e, batch.batchId)}
                                  className="action-button receive-btn"
                                  disabled={!!isMarkingReceived}
                                >
                                  {isMarkingReceived === batch.batchId ? 'Receiving...' : 'Mark as Received'}
                                </button>
                              )}
                              {canViewTimeline && isBatchEligibleForTimeline(batch) && (
                                <button
                                  type="button"
                                  onClick={(e) => handleToggleTimeline(e, batch.batchId)}
                                  className={`action-button timeline-btn ${isTimelineOpen ? 'active' : ''}`}
                                  title="View complete lifecycle timeline"
                                >
                                  {isTimelineOpen ? '▲ Timeline' : '⏱️ Timeline'}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>

                        {isTimelineOpen && isBatchEligibleForTimeline(batch) && (
                          <tr className="batch-timeline-accordion-row">
                            <td colSpan={totalCols} className="batch-timeline-td" onClick={(e) => e.stopPropagation()}>
                              <div className="batch-timeline-panel">
                                <div className="batch-timeline-header">
                                  <div className="batch-timeline-title">
                                    <span className="timeline-title-icon">⏱️</span>
                                    <h4>Batch Audit Timeline: <strong>{batch.batchId}</strong></h4>
                                  </div>
                                  <span className="batch-timeline-summary-tag">
                                    {batch.batchQuality} &bull; {batch.consolidatedItems.length} Items &bull; {batch.consolidatedItems.reduce((sum, item) => sum + (item.totalQuantity || 0), 0).toFixed(2)}m
                                  </span>
                                </div>
                                {(() => {
                                  const tState = timelinesData[batch.batchId];
                                  if (tState?.loading) {
                                    return <div className="batch-timeline-loading">Loading timeline history...</div>;
                                  }
                                  if (tState?.error) {
                                    return <div className="batch-timeline-error">Error: {tState.error}</div>;
                                  }
                                  const timeline = tState?.data || [];
                                  if (timeline.length === 0) {
                                    return <div className="batch-timeline-empty">No lifecycle events recorded yet for this batch.</div>;
                                  }
                                  return (
                                    <div className="batch-timeline-table-wrapper">
                                      <table className="batch-timeline-table">
                                        <thead>
                                          <tr>
                                            <th>Event / Stage</th>
                                            <th>Date & Time</th>
                                            <th className="center-col">Days from Created Date</th>
                                            <th>Performed By</th>
                                            <th>Details & Summary</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {timeline.map((item) => (
                                            <tr key={item.id} className={`timeline-row timeline-type-${String(item.type || '').toLowerCase()}`}>
                                              <td>
                                                <div className="timeline-stage-cell">
                                                  <span className={`timeline-icon-dot dot-${String(item.type || '').toLowerCase()}`} />
                                                  <strong>{item.stage}</strong>
                                                </div>
                                              </td>
                                              <td className="timeline-date-cell">
                                                {new Date(item.timestamp).toLocaleDateString('en-GB', {
                                                  day: '2-digit',
                                                  month: 'short',
                                                  year: 'numeric',
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                })}
                                              </td>
                                              <td className="center-col">
                                                <span className={`timeline-days-badge badge-${String(item.type || '').toLowerCase()}`}>
                                                  {item.daysLabel}
                                                </span>
                                              </td>
                                              <td className="timeline-actor-cell">{item.actor}</td>
                                              <td className="timeline-details-cell">{item.details}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                        );
                      })()
                    )) : (
                      <tr>
                        <td colSpan={(canPerformAction('batches.delete') || canPerformAction('batches.create') || canViewTimeline) ? 7 : 6}>
                          No batches found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {canViewPendingReport && activeTab === 'pending_report' && (
        <div className="pending-report-panel">
          <div className="pending-report-header">
            <div className="pending-report-title-area">
              <h2>Pending Production Batches Report</h2>
              <p className="pending-report-subtitle">
                Batches currently awaiting fabric receipt & allocation (sorted oldest first).
              </p>
            </div>
            <div className="pending-report-actions">
              <button
                type="button"
                className="action-btn target-settings-btn"
                onClick={handleOpenTargetModal}
              >
                ⚙️ Target Days
              </button>
              <button
                type="button"
                className="action-btn download-img-btn"
                onClick={handleDownloadImageReport}
                disabled={isDownloadingImage}
              >
                {isDownloadingImage ? 'Generating Image...' : '📷 Download WhatsApp Image'}
              </button>
              <button
                type="button"
                className="action-btn copy-img-btn"
                onClick={handleCopyImageToClipboard}
                disabled={isCopyingImage}
              >
                {isCopyingImage ? 'Copying...' : '📋 Copy to Clipboard'}
              </button>
              <button
                type="button"
                className="action-btn refresh-report-btn"
                onClick={fetchPendingReportData}
                disabled={isLoadingReport}
              >
                {isLoadingReport ? 'Refreshing...' : '🔄 Refresh Data'}
              </button>
            </div>
          </div>

          {copySuccessMsg && (
            <div className="report-success-alert">
              ✅ {copySuccessMsg}
            </div>
          )}

          {pendingReportData?.summary && (
            <div className="pending-report-kpis">
              <div className="kpi-card highlight-card">
                <span className="kpi-label">Total Pending Fabric</span>
                <span className="kpi-value">{pendingReportData.summary.totalPendingQty.toFixed(2)} m</span>
                <span className="kpi-sub">{pendingReportData.summary.totalPendingPieces} pcs across {pendingReportData.summary.totalBatches} batches</span>
              </div>
              <div className={`kpi-card ${pendingReportData.summary.criticalBatches > 0 ? 'critical-kpi-card' : ''}`}>
                <span className="kpi-label">🚨 Critical Delay (0 Recv)</span>
                <span className="kpi-value">{(pendingReportData.summary.criticalPendingQty || 0).toFixed(2)} m</span>
                <span className="kpi-sub">{pendingReportData.summary.criticalPendingPieces || 0} pcs ({pendingReportData.summary.criticalBatches || 0} batches)</span>
              </div>
              <div className={`kpi-card ${pendingReportData.summary.majorBatches > 0 ? 'warning-card' : ''}`}>
                <span className="kpi-label">⚠️ Major Pending (&ge;30%)</span>
                <span className="kpi-value">{(pendingReportData.summary.majorPendingQty || 0).toFixed(2)} m</span>
                <span className="kpi-sub">{pendingReportData.summary.majorPendingPieces || 0} pcs ({pendingReportData.summary.majorBatches || 0} batches)</span>
              </div>
              <div className={`kpi-card ${pendingReportData.summary.minorBatches > 0 ? 'minor-kpi-card' : ''}`}>
                <span className="kpi-label">🟡 Minor Tail (&lt;30%)</span>
                <span className="kpi-value">{(pendingReportData.summary.minorPendingQty || 0).toFixed(2)} m</span>
                <span className="kpi-sub">{pendingReportData.summary.minorPendingPieces || 0} pcs ({pendingReportData.summary.minorBatches || 0} batches)</span>
              </div>
              <div className="kpi-card ontrack-kpi-card">
                <span className="kpi-label">🟢 On Track</span>
                <span className="kpi-value">{(pendingReportData.summary.onTrackPendingQty || 0).toFixed(2)} m</span>
                <span className="kpi-sub">{pendingReportData.summary.onTrackPendingPieces || 0} pcs ({pendingReportData.summary.onTrackBatches || 0} batches)</span>
              </div>
            </div>
          )}

          <div className="pending-report-table-card">
            {isLoadingReport ? (
              <div className="report-loading">Loading pending batches report data...</div>
            ) : (!pendingReportData?.batches || pendingReportData.batches.length === 0) ? (
              <div className="report-empty">No pending batches found. All batches are received / completed!</div>
            ) : (
              <div className="table-responsive">
                <table className="pending-report-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center' }}>Batch & Quality</th>
                      <th>Age & Date</th>
                      <th className="num-col">Total Fabric</th>
                      <th className="num-col">Received Qty</th>
                      <th className="num-col">Pending Fabric</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingReportData.batches.map((b) => {
                      const rowClass = b.alertLevel === 'critical' ? 'batch-row-critical' : b.alertLevel === 'major' ? 'batch-row-major' : b.alertLevel === 'minor' ? 'batch-row-minor' : '';
                      return (
                        <tr key={b.batchId} className={rowClass}>
                          <td className="batch-id-cell">
                            <div className="batch-id-wrapper">
                              <button
                                type="button"
                                className="batch-link-btn"
                                onClick={() => navigate(`/batches/${b.batchId}`)}
                                title="Click to view and manage batch details"
                              >
                                {b.batchId} ↗
                              </button>
                              <div className="cell-sub-label">{b.batchQuality}</div>
                            </div>
                          </td>
                          <td className={`age-cell ${b.isDelayed ? 'delayed-age' : ''}`}>
                            <div className="main-val">
                              {b.age} days {b.isDelayed && <span className="overdue-tag">({b.overdueDays}d overdue)</span>}
                            </div>
                            <div className="cell-sub-label">{b.createdDate}</div>
                          </td>
                          <td className="num-col">
                            <div className="main-val">{b.totalQty.toFixed(2)}m</div>
                            <div className="cell-sub-label">({b.totalPieces} pcs)</div>
                          </td>
                          <td className="num-col">
                            {b.receivedQty > 0 ? (
                              <>
                                <div className="main-val">{b.receivedQty.toFixed(2)}m</div>
                                {b.latestReceiveDate && <div className="cell-sub-label">Last: {b.latestReceiveDate}</div>}
                              </>
                            ) : (
                              <span className="empty-cell-dash">-</span>
                            )}
                          </td>
                          <td className="num-col pending-highlight">
                            <div className="main-val"><strong>{b.pendingQty.toFixed(2)}m</strong></div>
                            <div className="cell-sub-label pending-sub">({b.pendingPieces} pcs pending)</div>
                          </td>
                          <td>
                            <span className={`status-pill ${b.status === 'Partially Received' ? 'status-partial' : 'status-created'}`}>
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Grand Total ({pendingReportData.batches.length} Batches)</strong></td>
                      <td className="num-col"><strong>{pendingReportData.summary?.totalFabricQty?.toFixed(2)}m ({pendingReportData.summary?.totalPieces} pcs)</strong></td>
                      <td className="num-col">
                        <strong>
                          {pendingReportData.summary?.totalReceivedQty > 0 ? `${pendingReportData.summary.totalReceivedQty.toFixed(2)}m` : '-'}
                        </strong>
                      </td>
                      <td className="num-col pending-highlight">
                        <strong>{pendingReportData.summary?.totalPendingQty?.toFixed(2)}m ({pendingReportData.summary?.totalPendingPieces} pcs)</strong>
                      </td>
                      <td>-</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {isTargetModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '480px', borderRadius: '12px', padding: '1.75rem' }}>
            <h2 style={{ color: '#1e293b', marginTop: 0, marginBottom: '0.5rem', fontSize: '1.35rem' }}>
              ⚙️ Batch Production Target Days (SLA)
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              Set expected production turnaround days per fabric quality. Batches exceeding these days will be flagged as delayed.
            </p>
            {targetSaveMsg && (
              <div style={{ backgroundColor: '#dcfce7', color: '#15803d', padding: '10px 12px', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem', fontWeight: '500' }}>
                ✅ {targetSaveMsg}
              </div>
            )}
            <form onSubmit={handleSaveTargets}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                    Polyester Quality (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={targetsConfig.Polyester ?? 3}
                    onChange={(e) => setTargetsConfig(prev => ({ ...prev, Polyester: parseInt(e.target.value, 10) || 1 }))}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                    Pure Quality (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={targetsConfig.Pure ?? 5}
                    onChange={(e) => setTargetsConfig(prev => ({ ...prev, Pure: parseInt(e.target.value, 10) || 1 }))}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                    Mixed Quality (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={targetsConfig.Mixed ?? 4}
                    onChange={(e) => setTargetsConfig(prev => ({ ...prev, Mixed: parseInt(e.target.value, 10) || 1 }))}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                    Default / Other (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={targetsConfig.default ?? 5}
                    onChange={(e) => setTargetsConfig(prev => ({ ...prev, default: parseInt(e.target.value, 10) || 1 }))}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem' }}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setIsTargetModalOpen(false)}
                  className="action-btn"
                  style={{ backgroundColor: '#edf2f7', color: '#334155', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  disabled={isSavingTargets}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ backgroundColor: '#1a365d', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  disabled={isSavingTargets}
                >
                  {isSavingTargets ? 'Saving...' : 'Save Targets'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreviewModal && (
        <Modal isOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} contentClassName="batch-preview-modal-content">
          <h2>Confirm Batch Creation</h2>
          <p>
            Review the items below for the new <strong>{batchQualityToCreate}</strong> batch.
          </p>
          <hr />
          {renderPreview()}
          <hr />
          <div className="modal-actions">
            <button onClick={() => setShowPreviewModal(false)} className="cancel-btn" disabled={isCreating}>Cancel</button>
            <button onClick={handleCreateBatch} disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Confirm & Create Batch'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default ProductionBatchPage;
